/**
 * Supabase Client Configuration
 *
 * Centralized Supabase client for all database operations
 * Uses environment variables for configuration
 */

import { createClient } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl) {
  throw new Error('Missing REACT_APP_SUPABASE_URL environment variable');
}

if (!supabaseAnonKey) {
  throw new Error('Missing REACT_APP_SUPABASE_ANON_KEY environment variable');
}

// Create Supabase client instance
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: {
      'x-application-name': 'subby'
    },
    fetch: (...args) => {
      // Log all fetch requests for debugging
      return fetch(...args);
    }
  }
});

/**
 * Get current user ID
 * Returns the authenticated user's ID from Supabase Auth
 */
export const getCurrentUserId = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error) {
      console.error('Error getting user ID:', error);
      throw error;
    }

    if (!user) {
      throw new Error('No authenticated user');
    }

    return user.id;
  } catch (error) {
    console.error('getCurrentUserId error:', error);
    throw error;
  }
};

/**
 * Check if Supabase is properly configured
 */
export const isSupabaseConfigured = () => {
  return !!(supabaseUrl && supabaseAnonKey);
};

/**
 * Test database connection
 */
export const testConnection = async () => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('count')
      .limit(1);

    if (error) {
      console.error('Supabase connection test failed:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Supabase connection test error:', err);
    return false;
  }
};

export default supabase;
